import Breadcrumb from "@/components/Breadcrumb";
import { useEffect, useRef, useState } from "react";
import TableComponent from "@/components/Table/TabelComponent";
import {
    ItemBusenissUnitDropDownNone,
    ItemDropDownAll,
    ItemDropDownNone,
} from "@/constants/default";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import queryString from "query-string";
import { dataQueryApi, dataQueryClient } from "@/utils/query";
import LimitPaginationComponent from "@/components/Table/LimitPaginationComponent";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useGetTransactionsQuery } from "@/services/transaction.service";
import TableHeader from "./_header";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import DropDownList from "@/components/Dropdown";
import { useGetDropDownTransactionTypesQuery } from "@/services/transactiontype.service";
import ButtonSpin, { ButtonSpinHandle } from "@/components/Button/ButtonSpin";
import { EnumStatus, TimeModeFilterTransaction } from "@/constants/enum";
import moment from "moment-timezone";
import "./style.css"
import { useGetDropDownUnitCodesQuery } from "@/services/unitcode.service";
import { useGetDropDownBusinessUnitsQuery } from "@/services/businessunit.service";
import { usePermissions } from "@/rbac";
import { permissions } from "@/rbac/permissions";

interface FilterForm {
    transaction_code: string
    business_unit: string
    transactiontype: string
    start_date: string
    end_date: string
    time_mode: string
}

const endDate = moment().endOf('day').toISOString()
const startDate = moment("2020-07-04", "YYYY-MM-DD").startOf('day').toISOString()
const defaultLimit = 50
const defaultSort: string = 'date'
const defaultOrder: string = "desc"

export default function Transactions() {
    const location = useLocation();
    const navigate = useNavigate();
    const parsed = queryString.parse(location?.search);
    const [searchParams, setSearchParams] = useSearchParams()
    const { hasPermission } = usePermissions();
    const limit = _.isString(parsed.l) ? parseInt(parsed?.l) : defaultLimit
    const queryParams = dataQueryApi(location?.search, {
        limit: defaultLimit,
        field: defaultSort,
        order: defaultOrder,
        type: parsed.type,
        business_unit: parsed.business_unit,
        code: parsed.code,
        start_date: moment(parsed.start_date?.toString(), 'YYYY-MM-DD').startOf('day').toISOString() || startDate,
        end_date: moment(parsed.end_date?.toString(), 'YYYY-MM-DD').endOf('day').toISOString() || endDate
    });
    const {data: transactions, refetch, isFetching, isLoading} = useGetTransactionsQuery(queryParams, {refetchOnMountOrArgChange: true});
    const {data: transactionTypes} = useGetDropDownTransactionTypesQuery(undefined,{ refetchOnMountOrArgChange: true });
    const {data: businessUnits} = useGetDropDownBusinessUnitsQuery({status: `${EnumStatus.ACTIVE},${EnumStatus.INAVTIVE}`},{ refetchOnMountOrArgChange: true })
    const {data: transactionCodes} = useGetDropDownUnitCodesQuery(undefined,{ refetchOnMountOrArgChange: true })
    const { t } = useTranslation();
    const [openFilter, setOpenFilter] = useState(true);
    const { register, handleSubmit, control, setValue, watch } = useForm<FilterForm>();
    const itemDropDownAll = ItemDropDownAll();
    const itemDropDownNone = ItemBusenissUnitDropDownNone();
    const btnSearchRef = useRef<ButtonSpinHandle>(null);
    const btnClearhRef = useRef<ButtonSpinHandle>(null);
    const timeMode = watch("time_mode")
    const limitPagination = (limit === 0 ? transactions?.total_records : limit) || 0
    

    const getData = (type: string, result: any) => {
        let data = dataQueryClient(location.search, {field: defaultSort, limit: defaultLimit, order: defaultOrder});
        switch (type) {
            case "search":
                data.s = result;
                data.p = "1";
                break;
            case "sort":
                data.o = result[0].desc ? "desc" : "asc";
                data.f = result[0].id;
                break;
            case "page":
                data.p = result;
                break;
            case "limit":
                data.l = result
                data.p = "1";
                break;
            default:
                data = { ...data };
                break;
        }
        setSearchParams(data)
        // navigate({
        //     search: createSearchParams(data).toString(),
        // });
    };

    const onSubmitFilter = handleSubmit((data) => {
        let search = dataQueryClient(location.search, {field: defaultSort, order: defaultOrder, limit: defaultLimit});
        search.business_unit = data.business_unit;
        search.code = data.transaction_code;
        search.type = data.transactiontype;
        search.mode = data.time_mode
        search.start_date = data.start_date
        search.end_date = data.end_date
        search.p = "1"
        !_.isEqual(search, queryString.parse(location?.search)) ? setSearchParams(search) : refetch()
    });

    const clearSearch = () => {
        setValue("transaction_code", itemDropDownAll.value)
        setValue("business_unit", itemDropDownAll.value)
        setValue("transactiontype", itemDropDownAll.value)
        setValue("time_mode", TimeModeFilterTransaction.CUSTOM)
        setValue("start_date", moment(startDate).format("YYYY-MM-DD"))
        setValue("end_date", moment(endDate).format("YYYY-MM-DD"))
        setSearchParams()
    }

    useEffect(() => {
        if (location.state?.reload) {
            refetch();
        }
    }, [location.state?.reload]);

    useEffect(() => {
        !isFetching && btnSearchRef.current && btnSearchRef.current.toggleSpin(false);
        !isFetching && btnClearhRef.current && btnClearhRef.current.toggleSpin(false);
    }, [isFetching]);

    useEffect(() => {
        if (timeMode) {
            let start_date = watch("start_date")
            let end_date = watch("end_date")
            switch (timeMode) {
                case TimeModeFilterTransaction.MONTH:
                    end_date = moment().endOf('day').format("YYYY-MM-DD")
                    start_date = moment().startOf('month').format("YYYY-MM-DD")
                    break;
                case TimeModeFilterTransaction.LAST_MONTH:
                    end_date = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
                    start_date = moment().subtract(1, 'month').startOf("month").format("YYYY-MM-DD")
                    break;
                case TimeModeFilterTransaction.CUSTOM:
                    end_date = moment(endDate).format("YYYY-MM-DD")
                    start_date = moment(startDate).format("YYYY-MM-DD")
                    break;
            }
            setValue("start_date", start_date)
            setValue("end_date", end_date)
        }
    }, [timeMode])

    useEffect(() => {
        setValue('time_mode', parsed.mode ? parsed.mode.toString() : TimeModeFilterTransaction.CUSTOM);
        setValue('start_date', parsed.start_date ? parsed.start_date.toString() : moment(startDate).format("YYYY-MM-DD"))
        setValue('end_date', parsed.end_date ? parsed.end_date.toString() : moment(endDate).format("YYYY-MM-DD"))
    }, [])

    useEffect(() => {
        if(businessUnits && businessUnits.length > 0 && parsed.business_unit) {
            let businessUnit = _.find(businessUnits, item => item.value === parsed.business_unit)
            businessUnit && setValue('business_unit', businessUnit.value)
        }
    }, [parsed.business_unit, businessUnits])

    useEffect(() => {
        if(transactionTypes && transactionTypes.length > 0 && parsed.type) {
            let transactiontype = _.find(transactionTypes, item => item.value === parsed.type)
            transactiontype && setValue('transactiontype', transactiontype.value)
        }
    }, [parsed.type, transactionTypes])

    useEffect(() => {
        if(transactionCodes && transactionCodes.length > 0 && parsed.code) {
            let transactionCode = _.find(transactionCodes, item => item.value === parsed.code)
            transactionCode && setValue('transaction_code', transactionCode.value)
        }
    }, [parsed.code, transactionCodes])

    return (
        <>
            <Breadcrumb pageName={t("transaction.transaction")} />
            <div className="flex flex-col gap-10">
                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
                    <div className="py-4 shadow-9 sm:py-6">
                        <button
                            type="button"
                            className="flex w-full items-center gap-1.5 sm:gap-3 xl:gap-6 active"
                            onClick={() => setOpenFilter(!openFilter)}
                        >
                            <div className="flex items-center justify-center ms-2">
                                <i className={classNames("fa-solid fa-chevron-right", {"fa-rotate-90": openFilter})} />
                            </div>
                            <h4 className="font-bold text-md">
                                {t("transaction.advance_search")}
                            </h4>
                        </button>
                        <div
                            className={`mt-5 duration-200 ease-in-out ${openFilter ? "block" : "hidden"}`}
                        >
                            <form className="mx-3" onSubmit={onSubmitFilter}>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="mb-1">
                                        <label className="mb-2.5 block dark:text-white">
                                            {t("transaction.business_unit")}
                                        </label>
                                        {
                                            businessUnits && <Controller
                                                name="business_unit"
                                                control={control}
                                                defaultValue={itemDropDownAll.value}
                                                render={({ field: { onChange, value } }) => <DropDownList
                                                    selected={value}
                                                    setSelected={onChange}
                                                    list={[itemDropDownAll, itemDropDownNone, ...businessUnits]}
                                                />}
                                            />
                                        }
                                    </div>
                                    <div className="mb-1">
                                        <label className="mb-2.5 block dark:text-white">
                                            {t("transaction.category")}
                                        </label>
                                        {transactionTypes && <Controller
                                            name="transactiontype"
                                            control={control}
                                            defaultValue={itemDropDownAll.value}
                                            render={({ field: { onChange, value } }) => <DropDownList
                                                selected={value}
                                                setSelected={onChange}
                                                list={[itemDropDownAll, ...transactionTypes]}
                                            />}
                                        />}
                                    </div>
                                    <div className="mb-1">
                                        <label className="mb-2.5 block dark:text-white">
                                            {t("transaction.transaction_code")}
                                        </label>
                                        {
                                            transactionCodes && <Controller
                                                name="transaction_code"
                                                control={control}
                                                defaultValue={itemDropDownAll.value}
                                                render={({ field: { onChange, value } }) => <DropDownList
                                                    selected={value}
                                                    setSelected={onChange}
                                                    list={[itemDropDownAll, ...transactionCodes]}
                                                />}
                                            />
                                        }
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-4">
                                    <Controller
                                        name="time_mode"
                                        control={control}
                                        defaultValue="month"
                                        render={({ field: { onChange, value } }) => {
                                            const classNameBtn = "inline-flex py-1 px-2 font-medium capitalize text-black hover:border-primary hover:bg-primary hover:text-white dark:text-white dark:hover:border-primary sm:py-2 sm:px-3"
                                            return <div>
                                                <button type="button" className={classNames(`border rounded-l ${classNameBtn}`, {"border-primary bg-primary text-white": value === TimeModeFilterTransaction.MONTH, "dark:border-strokedark border-stroke": value !== TimeModeFilterTransaction.MONTH})} value={TimeModeFilterTransaction.MONTH} onClick={onChange}>{TimeModeFilterTransaction.MONTH}</button>
                                                <button type="button" className={classNames(`border-y ${classNameBtn}`, {"border-primary bg-primary text-white": value === TimeModeFilterTransaction.LAST_MONTH, "dark:border-strokedark border-stroke": value !== TimeModeFilterTransaction.LAST_MONTH})} value={TimeModeFilterTransaction.LAST_MONTH} onClick={onChange}>{TimeModeFilterTransaction.LAST_MONTH}</button>
                                                <button type="button" className={classNames(`border rounded-r ${classNameBtn}`, {"border-primary bg-primary text-white": value === TimeModeFilterTransaction.CUSTOM, "dark:border-strokedark border-stroke": value !== TimeModeFilterTransaction.CUSTOM})} value={TimeModeFilterTransaction.CUSTOM} onClick={onChange}>{TimeModeFilterTransaction.CUSTOM}</button>
                                            </div>
                                        }}
                                    />
                                    <div className="flex items-center">
                                        <label className="block dark:text-white w-25">
                                            {t("transaction.start_date")}:
                                        </label>
                                        <input
                                            type="date"
                                            max={moment().format("YYYY-MM-DD")}
                                            className="custom-input-date-1 w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:opacity-75"
                                            {...register("start_date")}
                                            disabled={timeMode !== TimeModeFilterTransaction.CUSTOM}
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="block dark:text-white w-25">
                                            {t("transaction.end_date")}:
                                        </label>
                                        <input
                                            type="date"
                                            max={moment().format("YYYY-MM-DD")}
                                            className="custom-input-date-1 w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:opacity-75"
                                            {...register("end_date")}
                                            disabled={timeMode !== TimeModeFilterTransaction.CUSTOM}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-center mt-5 mb-5">
                                    <div className="me-3">
                                        <ButtonSpin
                                            ref={btnClearhRef}
                                            title={t("transaction.clear")}
                                            classBtn="flex justify-center items-center block w-full rounded border border-stroke bg-gray-400 p-1.5 px-3 text-center font-medium text-black transition hover:bg-opacity-90 disabled:opacity-50"
                                            type="button"
                                            icon={<i className="fa-light fa-trash me-3 text-black" />}
                                            isDisabled={location?.search === ""}
                                            onClick={clearSearch}
                                        />
                                    </div>
                                    <div>
                                        <ButtonSpin
                                            ref={btnSearchRef}
                                            title={t("transaction.search")}
                                            type="submit"
                                            icon={<i className="fa-light fa-magnifying-glass me-3" />}
                                            isDisabled={isFetching || isLoading}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2">
                        <div className="flex xs:flex-col flex-row">
                            <LimitPaginationComponent
                                list={[50, 100, 0]}
                                limit={limit}
                                onChange={(s: number) => getData("limit", s)}
                            />
                        </div>
                        <div className="flex justify-start md:justify-end mt-2 md:mt-0">
                            <div className="me-2">
                                <button
                                    className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 text text-white"
                                    onClick={refetch}
                                >
                                    <i className="fa-sharp fa-light fa-arrows-rotate me-2" />
                                    {t("table.reload")}
                                </button>
                            </div>
                            {
                                hasPermission(permissions.TRANSACTION.ADD) && <div>
                                    <button
                                        className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 text text-white"
                                        onClick={() =>
                                            navigate(location, {
                                                state: { modal: "/transactions/add" },
                                            })
                                        }
                                    >
                                        <i className="fa-light fa-plus me-2" />
                                        {t("table.add")}
                                    </button>
                                </div>
                            }
                        </div>
                    </div>
                    
                    <div className="max-w-full overflow-x-auto mt-3">
                        <TableComponent
                            data={transactions?.data || []}
                            columns={TableHeader()}
                            pageSize={limitPagination}
                            isPagination={true}
                            currentPage={_.isString(parsed?.p) ? parseInt(parsed?.p) - 1 : 0}
                            totalPages={limit === 0 ? 1 : Math.ceil((transactions?.total_records || 0) / limitPagination)}
                            onChangePage={(page: number) => getData("page", page + 1)}
                            isLoading={isFetching || isLoading}
                            sort={[
                                {
                                    id: parsed?.f?.toString() || defaultSort,
                                    desc: parsed?.o === defaultOrder ? true : false || true
                                }
                            ]}
                            onChangeSort={data => getData("sort", data)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
